import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  CardHeader,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useHistory, useParams } from "react-router-dom";
import Cropper from "react-easy-crop";

import "../../../Components/Common/crop.css";
import { getDLDealCroppedImg } from "../../../Components/Common/DealCropDL";

function AddLimitedTimeDeal() {
  const history = useHistory();
  const { businessId } = useParams();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    categoryId: "",
    startDate: "",
    endDate: "",
    dealPhoto: null,
  });

  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [dealPhotoPreview, setDealPhotoPreview] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("No file chosen");

  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}categories`);
        const data = await res.json();
        setCategories(data.responseData?.categories || []);
      } catch {
        toast.error("Failed to load categories");
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFileName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      setImageToCrop(reader.result);
      setShowCropper(true);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async () => {
    try {
      const blob = await getDLDealCroppedImg(imageToCrop, croppedAreaPixels);
      const file = new File([blob], "deal-photo.jpg", { type: "image/jpeg" });
      setFormData((prev) => ({ ...prev, dealPhoto: file }));
      setDealPhotoPreview(URL.createObjectURL(blob));
      setShowCropper(false);
    } catch {
      toast.error("Image crop failed");
    }
  };

  const handleRemovePhoto = () => {
    setFormData((prev) => ({ ...prev, dealPhoto: null }));
    setDealPhotoPreview(null);
    setSelectedFileName("No file chosen");
  };

  const toIsoOrEmpty = (dateStr) => {
    if (!dateStr) return "";
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!match) return "";
    const [, y, m, d] = match;
    return new Date(Date.UTC(Number(y), Number(m) - 1, Number(d))).toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!formData.categoryId) {
      toast.error("Category is required");
      return;
    }
    if (!formData.startDate) {
      toast.error("Start date is required");
      return;
    }
    if (!formData.endDate) {
      toast.error("End date is required");
      return;
    }
    if (new Date(formData.endDate) < new Date(formData.startDate)) {
      toast.error("End date must be after start date");
      return;
    }

    setLoading(true);

    try {
      if (!businessId) throw new Error("Business ID missing in URL");

      const payload = new FormData();
      payload.append("name", formData.name);
      if (formData.description) payload.append("description", formData.description);
      payload.append("categoryId", formData.categoryId);
      payload.append("businessId", businessId);

      const startIso = toIsoOrEmpty(formData.startDate);
      const endIso = toIsoOrEmpty(formData.endDate);
      if (startIso) payload.append("startDate", startIso);
      if (endIso) payload.append("endDate", endIso);

      if (formData.dealPhoto) payload.append("dealPhoto", formData.dealPhoto);

      const res = await fetch(
        `${process.env.REACT_APP_BASE_URL}admin/deal/limitedTimeDeals/${businessId}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
          },
          body: payload,
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.responseMessage?.join(", ") || "Server Error");

      toast.success("Limited Time Deal added successfully!");
      setTimeout(() => history.push(`/business-deals/${businessId}`), 1200);
    } catch (err) {
      toast.error(err.message || "Failed to add limited time deal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <MetaTags>
          <title>Add Limited Time Deal</title>
        </MetaTags>
        <ToastContainer />

        <Row>
          <Col lg="12">
            <Card>
              <CardHeader className="d-flex align-items-center">
                <Link to={`/business-deals/${businessId}`}>
                  <Button color="primary">
                    <i className="mdi mdi-arrow-left" />
                  </Button>
                </Link>
                <h4 className="mb-0 ms-2">Add Limited Time Deal</h4>
              </CardHeader>

              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Name *</Label>
                        <Input
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Deal Name"
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>Category *</Label>
                        <Input
                          type="select"
                          name="categoryId"
                          value={formData.categoryId}
                          onChange={handleInputChange}
                          disabled={categoriesLoading}
                        >
                          <option value="">Select category</option>
                          {categories.map((c) => (
                            <option key={c._id} value={c._id}>{c.name}</option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="textarea"
                          name="description"
                          value={formData.description}
                          onChange={handleInputChange}
                          rows={3}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Deal Photo</Label>
                        <div>
                          <Button type="button" color="primary" onClick={() => fileInputRef.current.click()}>
                            Choose File
                          </Button>
                          <span className="ms-2">{selectedFileName}</span>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            hidden
                          />
                        </div>
                        {dealPhotoPreview && (
                          <div style={{ position: 'relative', display: 'inline-block', marginTop: 8 }}>
                            <img src={dealPhotoPreview} alt="preview" className="rounded border" width="100" />
                            <span
                              onClick={handleRemovePhoto}
                              style={{
                                position: 'absolute',
                                top: -5,
                                right: -5,
                                background: '#ff3c3fff',
                                color: 'white',
                                borderRadius: '50%',
                                width: 20,
                                height: 20,
                                textAlign: 'center',
                                lineHeight: '20px',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: 14
                              }}
                            >
                              ×
                            </span>
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Start Date *</Label>
                        <Input
                          type="date"
                          name="startDate"
                          value={formData.startDate}
                          onChange={handleInputChange}
                        />
                      </FormGroup>
                    </Col>

                    <Col md={6}>
                      <FormGroup>
                        <Label>End Date *</Label>
                        <Input
                          type="date"
                          name="endDate"
                          value={formData.endDate}
                          onChange={handleInputChange}
                          min={formData.startDate || undefined}
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Button type="submit" color="primary" className="mt-4" disabled={loading}>
                    {loading ? "Submitting..." : "Submit"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>Crop Image</h4>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={() => setShowCropper(false)}
                >
                  &times;
                </button>
              </div>

              <div className="crop-container">
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={3 / 2}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              </div>

              <div className="crop-controls">
                <label>Zoom</label>
                <input
                  type="range"
                  min={1}
                  max={3}
                  step={0.1}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                />
              </div>

              <div className="crop-buttons">
                <button className="btn btn-secondary" onClick={() => setShowCropper(false)}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleCropSave}>
                  Save Crop
                </button>
              </div>
            </div>
          </div>
        )}

      </Container>
    </div>
  );
}

export default AddLimitedTimeDeal;
